import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  School,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchSchools, createSchool, updateSchoolAsync, deleteSchoolAsync } from '../../store/slices/schoolSlice';
import { School as SchoolType } from '../../types';
import { saveAs } from 'file-saver';
import schoolService from '../../services/schoolService';
import studentsService from '../../services/studentService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAccess from '../../hooks/useAccess';


type ColumnId = 'name' | 'location' | 'channel' | 'students' | 'subscriptions';

const COLUMNS: { id: ColumnId; label: string; numeric?: boolean }[] = [
  { id: 'name', label: 'School' },
  { id: 'location', label: 'Location' },
  { id: 'channel', label: 'Channel' },
  { id: 'students', label: 'Students', numeric: true },
  { id: 'subscriptions', label: 'Subscriptions', numeric: true },
];

/** Bucket every school starts in until a real channel is assigned. */
const DEFAULT_CHANNEL = 'edulabs';

/** Channels are stored lower case; show them capitalised. */
const formatChannel = (channel?: string) =>
  channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : '';

const SchoolsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { schools, isLoading, error } = useSelector((state: RootState) => state.schools);
  const { selected: selectedYear, current: currentYear } = useSelector((state: RootState) => state.academicYear);
  const access = useAccess();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSchool, setEditingSchool] = useState<SchoolType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    academicYear: '',
    salesChannelId: '',
  });
  const [channels, setChannels] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<ColumnId>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Set when arriving from a channel card; null means every channel.
  const channelFilter = searchParams.get('channel');
  const [studentCounts, setStudentCounts] = useState<{ [schoolId: string]: number }>({});
  const [subscriptionCounts, setSubscriptionCounts] = useState<{ [schoolId: string]: number }>({});

  useEffect(() => {
    dispatch(fetchSchools({ academicYear: selectedYear, channel: channelFilter }));
  }, [dispatch, selectedYear, channelFilter]);

  useEffect(() => {
    schoolService
      .getSalesChannels()
      .then((res) => setChannels(res.data))
      .catch(() => setChannels([]));
  }, []);

  useEffect(() => {
    if (schools.length === 0) return;

    const fetchCounts = async () => {
      const sc: { [schoolId: string]: number } = {};
      const sub: { [schoolId: string]: number } = {};
      await Promise.all(
        schools.map(async (school) => {
          try {
            const res = await studentsService.getStudentsBySchool(school.id);
            sc[school.id] = res.data.length;
            sub[school.id] = res.data.reduce((total: number, student: any) => {
              return total + (student.subjects?.length || 0);
            }, 0);
          } catch {
            sc[school.id] = 0;
            sub[school.id] = 0;
          }
        })
      );
      setStudentCounts(sc);
      setSubscriptionCounts(sub);
    };
    fetchCounts();
  }, [schools]);

  const handleOpenDialog = (school?: SchoolType) => {
    if (school) {
      setEditingSchool(school);
      setFormData({
        name: school.name,
        address: school.address || '',
        city: school.city || '',
        state: school.state || '',
        country: school.country || '',
        pincode: school.pincode?.toString() || '',
        academicYear: school.academicYear?.toString() || '',
        salesChannelId: school.salesChannelId || '',
      });
    } else {
      setEditingSchool(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        academicYear: (selectedYear ?? currentYear)?.toString() || '',
        salesChannelId: channelFilter || DEFAULT_CHANNEL,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSchool(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      academicYear: '',
      salesChannelId: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const schoolData = {
        ...formData,
        pincode: formData.pincode ? parseInt(formData.pincode) : undefined,
        academicYear: formData.academicYear ? parseInt(formData.academicYear) : undefined,
      };

      if (editingSchool) {
        await dispatch(updateSchoolAsync({
          id: editingSchool.id,
          schoolData: schoolData
        })).unwrap();
      } else {
        await dispatch(createSchool(schoolData)).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save school:', error);
      // Error is already handled by the slice
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this school?')) {
      try {
        await dispatch(deleteSchoolAsync(id)).unwrap();
      } catch (error) {
        console.error('Failed to delete school:', error);
        // Error is already handled by the slice
      }
    }
  };

  const handleExportSchools = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await schoolService.exportSchools(selectedYear);
      const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
      saveAs(blob, `schools_export_${timestamp}.xlsx`);
    } catch (err: any) {
      setExportError(err.message || 'Failed to export schools');
    } finally {
      setExporting(false);
    }
  };



  const handleSort = (columnId: ColumnId) => {
    if (orderBy === columnId) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrder(columnId === 'name' || columnId === 'location' || columnId === 'channel' ? 'asc' : 'desc');
    }
  };

  const filteredSchools = schools.filter(school =>
    school.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (school.city && school.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (school.state && school.state.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sortValue = (school: SchoolType, columnId: ColumnId): string | number => {
    switch (columnId) {
      case 'name':
        return (school.name || '').toLowerCase();
      case 'location':
        return `${school.city || ''} ${school.state || ''}`.trim().toLowerCase();
      case 'channel':
        return school.salesChannelId || '';
      case 'students':
        return studentCounts[school.id] ?? -1;
      case 'subscriptions':
        return subscriptionCounts[school.id] ?? -1;
    }
  };

  const sortedSchools = [...filteredSchools].sort((a, b) => {
    const left = sortValue(a, orderBy);
    const right = sortValue(b, orderBy);
    if (left === right) return 0;
    const comparison = left < right ? -1 : 1;
    return order === 'asc' ? comparison : -comparison;
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading schools...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            LiveOlympiad Schools
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all registered schools and their information
          </Typography>
          {selectedYear != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Chip
                size="small"
                label={`${selectedYear}${selectedYear === currentYear ? ' · Current' : ' · Previous'}`}
                color={selectedYear === currentYear ? 'primary' : 'default'}
              />
              {channelFilter && (
                <Chip
                  size="small"
                  color="secondary"
                  label={`Channel: ${formatChannel(channelFilter)}`}
                  onDelete={() => setSearchParams({})}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {filteredSchools.length} {filteredSchools.length === 1 ? 'school' : 'schools'}
                {searchTerm ? ` matching "${searchTerm}"` : ''}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleExportSchools}
            disabled={exporting}
            sx={{ minWidth: 140 }}
          >
            {exporting ? 'Exporting...' : 'Export Schools'}
          </Button>
          {access.canWrite && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            Add School
          </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search schools by name, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Schools Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSchools.map((school) => (
              <TableRow
                key={school.id}
                hover
                onClick={() => navigate(`/schools/${school.id}/students`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {school.name?.split(',')[0] || school.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Code: {school.schoolcode || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {school.city && school.state ? `${school.city}, ${school.state}` : school.city || school.state || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {school.pincode || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={formatChannel(school.salesChannelId) || 'Unassigned'}
                    variant={school.salesChannelId === DEFAULT_CHANNEL ? 'outlined' : 'filled'}
                    color={school.salesChannelId === DEFAULT_CHANNEL ? 'default' : 'secondary'}
                  />
                </TableCell>
                <TableCell align="right">
                  {studentCounts[school.id] !== undefined ? studentCounts[school.id] : '-'}
                </TableCell>
                <TableCell align="right">
                  {subscriptionCounts[school.id] !== undefined ? subscriptionCounts[school.id] : '-'}
                </TableCell>
                {/* Row click opens the students view, so actions must not bubble */}
                <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                  {access.canWrite ? (
                    <>
                      <IconButton size="small" onClick={() => handleOpenDialog(school)} sx={{ color: 'primary.main' }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(school.id)} sx={{ color: 'error.main' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      View only
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {filteredSchools.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <School sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No schools found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : channelFilter
                ? `No schools on the ${formatChannel(channelFilter)} channel for this academic year`
                : 'Get started by adding your first school'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit School Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSchool ? 'Edit School' : 'Add New School'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <TextField
                fullWidth
                label="School Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Pin Code"
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                helperText="6-digit pin code"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Academic Year"
                value={formData.academicYear}
                onChange={(e) => setFormData({ ...formData, academicYear: e.target.value.replace(/[^0-9]/g, '') })}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                helperText={
                  formData.academicYear && currentYear != null && parseInt(formData.academicYear) > currentYear
                    ? `Creates a new academic year — ${formData.academicYear} would become the current one`
                    : 'Year this school belongs to, e.g. 2025'
                }
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                select
                fullWidth
                label="Channel"
                value={formData.salesChannelId}
                onChange={(e) => setFormData({ ...formData, salesChannelId: e.target.value })}
                disabled={access.channel != null}
                sx={{ mb: 2 }}
                helperText={
                  formData.salesChannelId === DEFAULT_CHANNEL
                    ? 'Default channel — reassign once the owner is known'
                    : 'Channel this school belongs to'
                }
              >
                {channels.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {formatChannel(channel)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {editingSchool ? 'Update' : 'Add'} School
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolsPage; 